/**
 * Estado de la simulación y de la reproducción. Cambiar algo durante la reproducción no altera la
 * corrida: cualquier cambio genera una configuración nueva, identificada por su huella.
 */

import { create } from 'zustand';
import { CATALOGO } from '../../catalogo/catalogo';
import type { ConfiguracionDeEjecucion } from '../../nucleo/dominio/ejecucion';
import type { Espacio } from '../../nucleo/dominio/espacio';
import type { Observacion } from '../../nucleo/dominio/observacion';
import { instante } from '../../nucleo/dominio/tiempo';
import { prepararEjecucion } from '../../nucleo/escenarios/preparar';
import type { ResultadoEjecucion } from '../../nucleo/escenarios/replicas';
import type { ModeloEspacial } from '../../nucleo/espacial/compilador';
import { crearReproductor, type Reproductor } from '../../nucleo/reproduccion/reproductor';
import { clienteMotor, SimulacionCancelada } from '../servicios/motor';

export const VELOCIDADES = [60, 120, 300, 600] as const;

const cacheEjecucionId = new WeakMap<Espacio, string>();

/** Identificador de la ejecución que daría el espacio tal como está (memorizado por versión). */
export function ejecucionIdDe(espacio: Espacio): string {
  let id = cacheEjecucionId.get(espacio);
  if (id === undefined) {
    const p = prepararEjecucion(espacio, CATALOGO);
    id = p.ok ? p.config.ejecucionId : 'invalida';
    cacheEjecucionId.set(espacio, id);
  }
  return id;
}

/**
 * ¿El resultado calculado corresponde al espacio actual? `null` si no hay resultado. Si el espacio
 * cambió después de simular (layout, parámetros, demanda o semilla), la jornada calculada ya no
 * describe este espacio: no se reproduce hasta volver a simular.
 */
export function useResultadoVigente(espacio: Espacio | null): boolean | null {
  const resultado = useSimulacion((s) => s.resultado);
  if (!espacio || !resultado) return null;
  return ejecucionIdDe(espacio) === resultado.ejecucionId;
}

interface EstadoSimulacion {
  estado: 'inactiva' | 'calculando' | 'lista' | 'fallida' | 'bloqueada';
  progreso: { hechas: number; total: number };
  resultado: ResultadoEjecucion | null;
  config: ConfiguracionDeEjecucion | null;
  modelo: ModeloEspacial | null;
  observaciones: Observacion[];
  reproductor: Reproductor | null;
  t: number;
  reproduciendo: boolean;
  velocidad: number;
  simular: (espacio: Espacio) => Promise<void>;
  cancelar: () => void;
  reiniciar: () => void;
  setT: (t: number) => void;
  setReproduciendo: (r: boolean) => void;
  setVelocidad: (v: number) => void;
}

export const useSimulacion = create<EstadoSimulacion>((set, get) => ({
  estado: 'inactiva',
  progreso: { hechas: 0, total: 0 },
  resultado: null,
  config: null,
  modelo: null,
  observaciones: [],
  reproductor: null,
  t: 0,
  reproduciendo: false,
  velocidad: 300,
  simular: async (espacio) => {
    const prep = prepararEjecucion(espacio, CATALOGO);
    if (!prep.ok) {
      set({ estado: 'bloqueada', observaciones: prep.observaciones });
      return;
    }
    const { config, modelo } = prep;
    set({
      estado: 'calculando',
      progreso: { hechas: 0, total: config.replicas },
      reproduciendo: false,
      observaciones: prep.observaciones,
    });
    try {
      const resultado = await clienteMotor().simular(config, (hechas, total) =>
        set({ progreso: { hechas, total } }),
      );
      const eventos = resultado.visualizada?.eventos ?? [];
      const inicio = instante(1, Math.max(0, config.turno.inicio - 10 * 60));
      const fin = instante(config.dias, config.cierre);
      const reproductor =
        resultado.estado === 'completa'
          ? crearReproductor({
              eventos,
              estaciones: modelo.estaciones.map((e) => ({
                id: e.id,
                x: e.centro.x,
                z: e.centro.z,
                rotacion: e.rotacion,
              })),
              rutas: modelo.rutas,
              inicio,
              fin,
            })
          : null;
      set({
        estado: resultado.estado === 'completa' ? 'lista' : 'fallida',
        resultado,
        config,
        modelo,
        reproductor,
        t: inicio,
        reproduciendo: resultado.estado === 'completa',
      });
    } catch (e) {
      if (e instanceof SimulacionCancelada) {
        set({ estado: get().resultado ? 'lista' : 'inactiva', progreso: { hechas: 0, total: 0 } });
        throw e;
      }
      set({
        estado: 'fallida',
        resultado: null,
        reproductor: null,
        observaciones: [],
      });
      throw e;
    }
  },
  cancelar: () => clienteMotor().cancelar(),
  reiniciar: () =>
    set({
      estado: 'inactiva',
      progreso: { hechas: 0, total: 0 },
      resultado: null,
      config: null,
      modelo: null,
      observaciones: [],
      reproductor: null,
      t: 0,
      reproduciendo: false,
    }),
  setT: (t) => {
    const r = get().reproductor;
    if (!r) return;
    const acotado = Math.min(r.fin, Math.max(r.inicio, t));
    set({ t: acotado, reproduciendo: acotado >= r.fin ? false : get().reproduciendo });
  },
  setReproduciendo: (reproduciendo) => {
    const r = get().reproductor;
    if (reproduciendo && r && get().t >= r.fin) set({ t: r.inicio });
    set({ reproduciendo });
  },
  setVelocidad: (velocidad) => set({ velocidad }),
}));
